import React, { useState, useEffect } from 'react';
import ProductAutocomplete from '../../../components/ProductAutocomplete';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { usePublications, CreatePublicationData, PublicationType } from '../../../hooks/usePublications';
import { usePublicationDetail } from '../../../hooks/usePublicationDetail';
import { useTenant } from '../../../contexts/TenantContext';
import { api } from '../../../services/api';
import { mediaThumbnail, mediaIcon, mediaLabel } from '../../customer/feed/PublicationMedia';
import './PublicationEditor.css';
import { logger } from '../../../utils/logger';

type VideoDraft = NonNullable<CreatePublicationData['videos']>[number];

interface PublicationEditorProps {
  tenantId?: string;
  vendorId?: string;
}

const PublicationEditor: React.FC<PublicationEditorProps> = ({ tenantId: propTenantId }) => {
  const { tenant } = useTenant();

  const tenantId = propTenantId || tenant?.id || '';
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { publication, loading: loadingDetail } = usePublicationDetail(id || '', tenantId);
  const { create, update, publish } = usePublications({}, tenantId);

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreatePublicationData>({
    type: 'ARRIVAGE',
    title: '',
    content: '',
    summary: '',
    images: [],
    videos: [],
    products: [],
    expires_at: '',
  });

  /**
   * Le formulaire ne garde que des identifiants de produits ; il faut leurs noms
   * pour les afficher. On les collecte au fil des sélections, et on les reprend
   * de la publication en mode édition.
   */
  const [nomsProduits, setNomsProduits] = useState<Record<string, string>>({});
  const [rechercheProduit, setRechercheProduit] = useState('');

  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [resolvingVideo, setResolvingVideo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  useEffect(() => {
    if (publication && isEditMode) {
      setFormData({
        type: publication.type,
        title: publication.title,
        content: publication.content,
        summary: publication.summary || '',
        images: publication.images || [],
        videos: publication.videos?.map((v, index) => ({
          url: v.url,
          thumbnail_url: v.thumbnail_url || undefined,
          caption: v.caption || undefined,
          display_order: v.display_order ?? index,
        })) || [],
        products: publication.products?.map((p) => ({
          product_id: p.product_id,
          highlight_price: p.highlight_price,
          special_offer: p.special_offer || '',
        })) || [],
        expires_at: publication.expires_at || '',
      });
      setNomsProduits(
        Object.fromEntries(
          (publication.products || []).map((p) => [p.product_id, p.product?.name || p.product_id]),
        ),
      );

    }
  }, [publication, isEditMode]);

  const handleSubmit = async (publishNow: boolean = false) => {
    if (!formData.title || !formData.content) {
      alert('Le titre et le contenu sont obligatoires');
      return;
    }

    setSaving(true);

    try {
      // Nettoyer les données : supprimer les champs vides/optionnels
      const cleanedData = {
        ...formData,
        ...(formData.expires_at ? {} : { expires_at: undefined }),
      };

      // Supprimer les clés undefined pour ne pas les envoyer à l'API
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key as keyof typeof cleanedData] === undefined || cleanedData[key as keyof typeof cleanedData] === '') {
          delete cleanedData[key as keyof typeof cleanedData];
        }
      });

      if (isEditMode && id) {
        const updated = await update(id, cleanedData);

        if (publishNow && updated.status === 'DRAFT') {
          await publish(updated.id);
          alert('Publication publiée et abonnés notifiés !');
        } else {
          alert('Publication mise à jour');
        }
      } else {
        const created = await create(cleanedData);

        if (publishNow) {
          await publish(created.id);
          alert('Publication créée et publiée !');
        } else {
          alert('Brouillon sauvegardé');
        }
      }

      navigate('/publications');
    } catch (error: any) {
      alert('Erreur : ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = () => {
    if (!imageUrl) return;

    setFormData({
      ...formData,
      images: [
        ...(formData.images || []),
        {
          url: imageUrl,
          display_order: (formData.images || []).length,
        },
      ],
    });

    setImageUrl('');
  };

  const handleRemoveImage = (index: number) => {
    setFormData({
      ...formData,
      images: formData.images?.filter((_, i) => i !== index),
    });
  };

  const appendVideo = (video: VideoDraft) => {
    setFormData((current) => ({
      ...current,
      videos: [
        ...(current.videos || []),
        { ...video, display_order: (current.videos || []).length },
      ],
    }));
  };

  const handleRemoveVideo = (index: number) => {
    setFormData({
      ...formData,
      videos: formData.videos?.filter((_, i) => i !== index),
    });
  };

  /**
   * Lien collé (YouTube, Vimeo, Ausha, .mp4) : le serveur le reconnaît et renvoie
   * l'URL à enregistrer + vignette + titre. Un smartlink Ausha (page de choix
   * Spotify/Deezer…) devient ainsi l'URL du lecteur, seule embarquable.
   */
  const handleAddVideoUrl = async () => {
    const url = videoUrl.trim();
    if (!url) return;

    try {
      setResolvingVideo(true);
      const preview = await api.get('/publications/media-preview', {
        params: { url },
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });
      appendVideo({
        url: preview.url,
        thumbnail_url: preview.thumbnail_url || undefined,
        caption: preview.title || undefined,
      });
      setVideoUrl('');
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || 'Lien non reconnu');
    } finally {
      setResolvingVideo(false);
    }
  };

  const handleUploadVideo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      alert('Veuillez sélectionner une vidéo (MP4, MOV, WebM)');
      event.target.value = '';
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      alert('La vidéo est trop volumineuse (max 200 Mo). Réduisez-la en 720p ou publiez-la sur YouTube et collez le lien.');
      event.target.value = '';
      return;
    }

    try {
      setUploadingVideo(true);
      setVideoProgress(0);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // axios direct, comme pour les images : le client API force un Content-Type JSON
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const token = localStorage.getItem('auth_token');

      const response = await axios.post(`${apiBaseUrl}/uploads/video`, formDataUpload, {
        headers: {
          ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 15 * 60 * 1000, // 200 Mo sur une liaison lente
        onUploadProgress: (progress) => {
          if (progress.total) {
            setVideoProgress(Math.round((progress.loaded / progress.total) * 100));
          }
        },
      });

      const uploadedUrl = response.data?.url;
      if (!uploadedUrl) {
        throw new Error('URL de la vidéo non reçue de l\'API');
      }
      appendVideo({ url: uploadedUrl });
    } catch (error: any) {
      logger.error('Erreur upload vidéo:', error);
      const message =
        error.response?.status === 413
          ? 'Vidéo refusée par le serveur : trop volumineuse.'
          : error.response?.data?.message || error.message;
      alert('Erreur lors de l\'upload de la vidéo : ' + message);
    } finally {
      setUploadingVideo(false);
      setVideoProgress(0);
      event.target.value = '';
    }
  };

  const handleUploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image valide');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop volumineuse (max 5MB)');
      return;
    }

    try {
      setUploading(true);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Utiliser axios directement pour éviter les intercepteurs qui modifient Content-Type
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const token = localStorage.getItem('auth_token');

      const uploadUrl = `${apiBaseUrl}/uploads/image`;
      logger.log('[PublicationEditor] Upload URL:', uploadUrl);

      const response = await axios.post(uploadUrl, formDataUpload, {
        headers: {
          'X-Tenant-ID': tenantId,
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      logger.log('Upload response COMPLETE:', response);
      logger.log('Upload response.data:', response.data);
      logger.log('Upload response.status:', response.status);

      // Vérifier que la réponse existe
      if (!response || !response.data) {
        throw new Error('Réponse API invalide ou vide');
      }

      // L'API retourne directement { url, filename, ... }
      const uploadedUrl = response.data.url;
      logger.log('[PublicationEditor] Uploaded URL from API:', uploadedUrl);

      if (!uploadedUrl) {
        logger.error('Structure de réponse:', response.data);
        throw new Error('URL de l\'image non reçue de l\'API');
      }

      // Construire l'URL complète
      // L'API retourne un chemin comme "/uploads/tenant-id/images/file.jpg"
      // Les fichiers statiques sont servis sur http://localhost:3000/uploads/ (sans /api/)
      const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';
      const fullUrl = uploadedUrl.startsWith('http')
        ? uploadedUrl
        : `${baseUrl}${uploadedUrl}`;

      logger.log('[PublicationEditor] Full image URL:', fullUrl);

      setFormData({
        ...formData,
        images: [
          ...(formData.images || []),
          {
            url: fullUrl,
            display_order: (formData.images || []).length,
          },
        ],
      });

      alert('Image uploadée avec succès !');
    } catch (error: any) {
      logger.error('Erreur upload:', error);
      logger.error('Response data:', error.response?.data);
      alert('Erreur lors de l\'upload de l\'image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
      // Réinitialiser l'input
      event.target.value = '';
    }
  };

  const publicationTypes: { value: PublicationType; label: string; icon: string; description: string }[] = [
    {
      value: 'ARRIVAGE',
      label: 'Arrivage',
      icon: '🍅',
      description: 'Nouveaux produits frais',
    },
    {
      value: 'PROMOTION',
      label: 'Promotion',
      icon: '🎉',
      description: 'Offres spéciales',
    },
    {
      value: 'ANTI_GASPI',
      label: 'Anti-gaspi',
      icon: '🌱',
      description: 'Produits à écouler',
    },
    {
      value: 'EVENEMENT',
      label: 'Événement',
      icon: '🎪',
      description: 'Dégustation, visite...',
    },
    {
      value: 'INFO',
      label: 'Information',
      icon: 'ℹ️',
      description: 'Info générale',
    },
  ];

  if (loadingDetail && isEditMode) {
    return (
      <div className="publication-editor">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="publication-editor">
      {/* Header */}
      <div className="editor-header">
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Retour
        </button>
        <h1>{isEditMode ? 'Modifier la publication' : 'Nouvelle publication'}</h1>
        <div className="header-actions">
          <button
            onClick={() => handleSubmit(false)}
            disabled={saving}
            className="btn-save-draft"
          >
            {saving ? 'Sauvegarde...' : '💾 Sauvegarder le brouillon'}
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={saving}
            className="btn-publish"
          >
            {saving ? 'Publication...' : '📢 Publier maintenant'}
          </button>
        </div>
      </div>

      <div className="editor-content">
        {/* Formulaire principal */}
        <div className="editor-main">
          {/* Type de publication */}
          <div className="form-group">
            <label className="form-label">Type de publication *</label>
            <div className="type-selector">
              {publicationTypes.map((type) => (
                <div
                  key={type.value}
                  className={`type-option ${formData.type === type.value ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, type: type.value })}
                >
                  <span className="type-icon">{type.icon}</span>
                  <span className="type-label">{type.label}</span>
                  <span className="type-description">{type.description}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Titre */}
          <div className="form-group">
            <label className="form-label">Titre *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: Arrivage de tomates bio du producteur local"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={100}
            />
            <div className="form-hint">{formData.title.length}/100 caractères</div>
          </div>

          {/* Résumé */}
          <div className="form-group">
            <label className="form-label">Résumé (optionnel)</label>
            <input
              type="text"
              className="form-input"
              placeholder="Courte description pour les notifications"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              maxLength={150}
            />
            <div className="form-hint">Affiché dans les notifications push</div>
          </div>

          {/* Contenu */}
          <div className="form-group">
            <label className="form-label">Contenu *</label>
            <textarea
              className="form-textarea"
              placeholder="Décrivez votre publication en détail..."
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={8}
            />
          </div>

          {/* Images */}
          <div className="form-group">
            <label className="form-label">Images</label>
            <div className="images-section">
              {formData.images && formData.images.length > 0 && (
                <div className="images-grid">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image-item">
                      <img src={image.url} alt={image.caption || ''} />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="btn-remove-image"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Upload de fichier */}
              <div className="image-upload-group">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleUploadImage}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image-upload" className={`btn-upload-image ${uploading ? 'uploading' : ''}`}>
                  {uploading ? '⏳ Upload en cours...' : '📁 Uploader une image'}
                </label>
              </div>

              <div className="divider">ou</div>

              {/* URL manuelle */}
              <div className="image-input-group">
                <input
                  type="url"
                  className="form-input"
                  placeholder="URL de l'image"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
                <button onClick={handleAddImage} className="btn-add-image">
                  ➕ Ajouter
                </button>
              </div>
              <div className="form-hint">
                💡 Astuce : Utilisez des images de haute qualité (16:9 recommandé, max 5MB)
              </div>
            </div>
          </div>

          {/* Vidéo / podcast */}
          <div className="form-group">
            <label className="form-label">Vidéo ou podcast</label>
            <div className="images-section">
              {formData.videos && formData.videos.length > 0 && (
                <div className="videos-list">
                  {formData.videos.map((video, index) => {
                    const thumb = mediaThumbnail(video);
                    return (
                      <div key={index} className="video-item">
                        <div className="video-thumb">
                          {thumb ? <img src={thumb} alt="" /> : <span>{mediaIcon(video)}</span>}
                        </div>
                        <div className="video-meta">
                          <div className="video-kind">{mediaIcon(video)} {mediaLabel(video)}</div>
                          <div className="video-caption">{video.caption || video.url}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveVideo(index)}
                          className="btn-remove-video"
                          title="Retirer"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Upload de fichier vidéo */}
              <div className="image-upload-group">
                <input
                  type="file"
                  id="video-upload"
                  accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                  onChange={handleUploadVideo}
                  disabled={uploadingVideo}
                  style={{ display: 'none' }}
                />
                <label htmlFor="video-upload" className={`btn-upload-image ${uploadingVideo ? 'uploading' : ''}`}>
                  {uploadingVideo ? `⏳ Envoi ${videoProgress}%…` : '🎬 Envoyer une vidéo (MP4, MOV)'}
                </label>
              </div>

              <div className="divider">ou</div>

              {/* Lien YouTube / Vimeo / Ausha */}
              <div className="image-input-group">
                <input
                  type="url"
                  className="form-input"
                  placeholder="Lien YouTube, Vimeo, Ausha, ou fichier .mp4 / .mp3"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddVideoUrl();
                    }
                  }}
                  disabled={resolvingVideo}
                />
                <button
                  type="button"
                  onClick={handleAddVideoUrl}
                  className="btn-add-image"
                  disabled={resolvingVideo || !videoUrl.trim()}
                >
                  {resolvingVideo ? '⏳' : '➕ Ajouter'}
                </button>
              </div>
              <div className="form-hint">
                💡 Un fichier envoyé ici doit être en H.264 (max 200 Mo, ~1 min en 720p) : les vidéos d'iPhone
                en HEVC sont refusées car Android et Chrome ne les lisent pas — convertissez-les d'abord
                (<code>scripts/convert-video-h264.sh</code>) ou réglez l'iPhone sur « Le plus compatible ».
                Pour une vidéo longue, YouTube reste le plus simple et le plus léger pour les clients en 4G.
              </div>
            </div>
          </div>

          {/* Produits de la note.
              La table qui lie une actualité à des produits existait depuis 2025
              et n'avait JAMAIS servi : il manquait ce champ. Six au maximum —
              au-delà ce n'est plus une histoire, c'est un catalogue, et il faut
              les photographier. */}
          <div className="form-group">
            <label className="form-label">Produits de la note (optionnel)</label>
            {(formData.products?.length || 0) < 6 && (
              <ProductAutocomplete
                value={rechercheProduit}
                onChange={setRechercheProduit}
                onProductSelect={(p) => {
                  const deja = (formData.products || []).some((x) => x.product_id === p.id);
                  if (!deja) {
                    setFormData({
                      ...formData,
                      products: [
                        ...(formData.products || []),
                        { product_id: p.id, highlight_price: true, special_offer: '' },
                      ],
                    });
                    setNomsProduits((n) => ({ ...n, [p.id]: p.name }));
                  }
                  setRechercheProduit('');
                }}
                placeholder="Chercher un produit à attacher…"
              />
            )}

            {(formData.products?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(formData.products || []).map((p) => (
                  <span
                    key={p.product_id}
                    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-300 text-sm"
                  >
                    {nomsProduits[p.product_id] || p.product_id}
                    <button
                      type="button"
                      aria-label="Retirer ce produit"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          products: (formData.products || []).filter(
                            (x) => x.product_id !== p.product_id,
                          ),
                        })
                      }
                      className="text-emerald-700 hover:text-emerald-900 font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="form-hint">
              {(formData.products?.length || 0) >= 6
                ? 'Six articles, c’est le maximum : au-delà, ce n’est plus une note, c’est un catalogue.'
                : 'Trois à six articles, choisis et photographiés. Un produit sans photo de produit s’affichera sans image — la photo d’étiquette prise à la réception ne compte pas, c’est de la traçabilité.'}
            </div>
          </div>

          {/* Date d'expiration */}
          <div className="form-group">
            <label className="form-label">Date d'expiration (optionnel)</label>
            <input
              type="datetime-local"
              className="form-input"
              value={
                formData.expires_at
                  ? new Date(formData.expires_at).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) =>
                setFormData({ ...formData, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })
              }
            />
            <div className="form-hint">
              La publication sera automatiquement masquée après cette date
            </div>
          </div>
        </div>

        {/* Panneau latéral - Prévisualisation */}
        <div className="editor-sidebar">
          <div className="preview-card">
            <h3 className="preview-title">📱 Prévisualisation</h3>
            <div className="preview-mobile">
              <div className="preview-type-badge" style={{ background: getTypeBadgeColor(formData.type) }}>
                {publicationTypes.find((t) => t.value === formData.type)?.icon}{' '}
                {publicationTypes.find((t) => t.value === formData.type)?.label}
              </div>
              {formData.images && formData.images.length > 0 ? (
                <img src={formData.images[0].url} alt="" className="preview-image" />
              ) : formData.videos && formData.videos.length > 0 ? (
                <div className="preview-media">
                  {mediaThumbnail(formData.videos[0]) && (
                    <img src={mediaThumbnail(formData.videos[0])} alt="" />
                  )}
                  <span>▶ {mediaLabel(formData.videos[0])}</span>
                </div>
              ) : null}
              <div className="preview-content">
                <h4>{formData.title || 'Titre de la publication'}</h4>
                <p>
                  {formData.summary || formData.content.substring(0, 100) || 'Contenu...'}
                  {formData.content.length > 100 && '...'}
                </p>
              </div>
            </div>
          </div>

          <div className="tips-card">
            <h3>💡 Conseils</h3>
            <ul>
              <li>Utilisez un titre accrocheur</li>
              <li>Ajoutez des images de qualité</li>
              <li>Une vidéo courte (30 s à 1 min) vaut mieux qu'une longue</li>
              <li>Mentionnez les prix si c'est une promo</li>
              <li>Publiez aux heures de pointe (8h, 12h, 18h)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const getTypeBadgeColor = (type: string) => {
  const colors: Record<string, string> = {
    ARRIVAGE: '#10b981',
    PROMOTION: '#f59e0b',
    ANTI_GASPI: '#22c55e',
    EVENEMENT: '#8b5cf6',
    INFO: '#3b82f6',
  };
  return colors[type] || '#6b7280';
};

export default PublicationEditor;
